// player/chapters.ts — chapters from timestamps in a video post's description (owner ruling
// 2026-09-26: "0:00 Intro" lines, the way YouTube does it; editing the post edits them).
//
// Rules, kept strict so ordinary text with a time in it never turns into chapters:
//   * a chapter is a line that STARTS with a timestamp (m:ss, mm:ss or h:mm:ss), optionally
//     after a bullet, then a title — "0:00 Intro", "- 1:32 The city", "01:02:03 – Credits";
//   * the first chapter starts at 0:00, times go strictly up and stay inside the video;
//   * at least two chapters, each at least MIN_CHAPTER_SECONDS long.
// Anything else → no chapters (an empty list), never a partial guess.

export interface Chapter {
  /** Seconds. */
  start: number;
  /** Seconds — the next chapter's start, or the video's end. */
  end: number;
  title: string;
}

export const MIN_CHAPTER_SECONDS = 5;
const MAX_TITLE = 80;

const LINE = /^\s*(?:[-•*–—]\s*)?(\d{1,2}(?::\d{2}){1,2})\s*(?:[-–—:|]\s*)?(.+?)\s*$/;

/** "1:02:03" → 3723; null when malformed (minutes/seconds must be under 60). */
export function parseTimestamp(raw: string): number | null {
  const parts = raw.split(':').map((p) => Number(p));
  if (parts.some((n) => !Number.isInteger(n) || n < 0)) return null;
  const [a, b, c] = parts;
  if (parts.length === 2) {
    if (b! >= 60) return null;
    return a! * 60 + b!;
  }
  if (parts.length === 3) {
    if (b! >= 60 || c! >= 60) return null;
    return a! * 3600 + b! * 60 + c!;
  }
  return null;
}

export function parseChapters(description: string, durationSec: number): Chapter[] {
  if (!description || !(durationSec > 0)) return [];
  const marks: { start: number; title: string }[] = [];
  for (const line of description.split('\n')) {
    const m = LINE.exec(line);
    if (!m) continue;
    const start = parseTimestamp(m[1]!);
    const title = m[2]!.trim().slice(0, MAX_TITLE);
    if (start === null || !title) continue;
    marks.push({ start, title });
  }
  if (marks.length < 2 || marks[0]!.start !== 0) return [];
  const chapters: Chapter[] = [];
  for (let i = 0; i < marks.length; i++) {
    const { start, title } = marks[i]!;
    const end = i + 1 < marks.length ? marks[i + 1]!.start : durationSec;
    if (start >= durationSec || end - start < MIN_CHAPTER_SECONDS) return [];
    chapters.push({ start, end, title });
  }
  return chapters;
}

/** The chapter playing at `t` seconds, or undefined. */
export function chapterAt(chapters: Chapter[], t: number): Chapter | undefined {
  return chapters.find((c) => t >= c.start && t < c.end) ?? chapters[chapters.length - 1];
}

/** 83 → "1:23", 3723 → "1:02:03" (the same shape the parser accepts). */
export function formatClock(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = String(s % 60).padStart(2, '0');
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${sec}` : `${m}:${sec}`;
}

/** "?t=" in a shared link: seconds ("134"), a clock ("2:14") or "2m14s". */
export function parseStartParam(raw: string | null): number | undefined {
  if (!raw) return undefined;
  if (/^\d+$/.test(raw)) return Number(raw);
  const clock = parseTimestamp(raw);
  if (clock !== null) return clock;
  const m = /^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/.exec(raw);
  if (m && (m[1] || m[2] || m[3])) {
    return Number(m[1] ?? 0) * 3600 + Number(m[2] ?? 0) * 60 + Number(m[3] ?? 0);
  }
  return undefined;
}
